import React, { useEffect } from "react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarFooter,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import logoCYT from "/images/logo-white.png";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import ReportBug from "@/components/forms/ReportBug";
import { signOut } from "@/_actions/authActions";
import usePlanStore from "@/stores/planStore";

import {
  FileUser,
  MapPinHouse,
  Binoculars,
  ChartPie,
  Users,
  User,
  Blocks,
  CreditCard,
  UserSearch,
  House,
  LogOut,
  MessagesSquare,
} from "lucide-react";

import { useNavigate, Link, useLocation } from "react-router";
import { useProfile } from "@/_actions/userActions";
import { usePlan } from "@/_actions/authActions";
import { useMyPropertiesCount } from "@/_actions/propertiesActions";

const navigation = [
  {
    name: "Inicio",
    href: "/inicio",
    icon: House,
    plan: ["all"],
    features: ["home"],
    role: ["owner", "admin", "broker"],
    customRules: [],
  },
  {
    name: "Oportunidades",
    href: "/contacts",
    icon: FileUser,
    plan: ["all"],
    features: ["contact_inbox"],
    role: ["owner", "admin", "broker"],
    customRules: [],
  },
  {
    name: "Propiedades",
    href: "/properties",
    icon: MapPinHouse,
    plan: ["all"],
    role: ["owner", "admin", "broker"],
    customRules: [],
  },
  {
    name: "Cuenta",
    href: "/profile",
    icon: User,
    plan: ["all"],
    role: ["owner", "admin", "broker"],
    customRules: [],
  },
  {
    name: "Reportes",
    href: "/reports",
    icon: ChartPie,
    plan: ["pro", "mid"],
    addon: "client_reports",
    role: ["owner", "admin", "broker"],
    features: ["client_reports"],
    customRules: [],
  },
  {
    name: "Usuarios",
    href: "/users",
    icon: Users,
    plan: ["all"],
    role: ["owner", "admin"],
    customRules: [],
  },
  {
    name: "Inventario CYT",
    href: "/inventory",
    icon: Binoculars,
    plan: ["pro", "mid"],
    addon: "cyt_inventory",
    role: ["owner", "admin", "broker"],
    customRules: [],
  },
  {
    name: "Integraciones",
    href: "/integrations",
    icon: Blocks,
    plan: ["pro", "mid"],
    addon: "crm_integration",
    role: ["owner", "admin"],
    customRules: [],
  },
  {
    name: "Subscripción",
    href: "/subscription",
    icon: CreditCard,
    plan: ["all"],
    role: ["owner"],
  },
  {
    name: "Investigaciones",
    href: "/investigaciones",
    id: "Menu-Investigaciones",
    icon: UserSearch,
    plan: ["all"],
    role: ["owner", "admin", "broker"],
    customRules: [],
  },
];
export function AppSidebar() {
  const { data: profile, mutate: mutateProfile } = useProfile();
  const { data: planData } = usePlan();
  const plan = usePlanStore((state) => state.plan);
  const { data: allPropertiesCount } = useMyPropertiesCount();

  useEffect(() => {
    if (planData) {
      usePlanStore.setState({
        plan: planData.plan,
        transaction: planData.transaction,
        contract: planData.contract,
        contractStatus: planData.contract_status,
        contractEndDate: planData.contract_end_date,
      });
    }
  }, [planData]);

  useEffect(() => {
    if (allPropertiesCount) {
      usePlanStore.setState({
        canPublish: allPropertiesCount?.can_publish,
      });
    }
  }, [allPropertiesCount]);

  const { pathname } = useLocation();
  const role = profile?.role;
  const navigate = useNavigate();

  const shouldShowNavItem = (item, plan, role) => {
    if (!item.role.includes(role)) {
      return false;
    }

    if (plan?.plan_addons[item.addon]) {
      return true;
    }

    if (item.plan.includes("all")) {
      return true;
    }

    if (item.plan.includes(plan?.name)) {
      return true;
    }

    return false;
  };

  return (
    <Sidebar variant="sidebar" className="dark bg-sidebar text-sidebar-foreground">
      <SidebarHeader className="border-b border-sidebar-border">
        <div className="flex items-center px-4 py-4">
          <img className="w-auto h-12" src={logoCYT} alt="CYT Pro Logo" />
        </div>
      </SidebarHeader>

      <SidebarContent className="flex-1">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navigation.map((item) =>
                shouldShowNavItem(item, plan, role) ? (
                  <SidebarMenuItem key={item.name}>
                    <SidebarMenuButton asChild isActive={item.href === pathname}>
                      <Link to={item.href} id={item?.id}>
                        {item?.isSvg ? (
                          <img
                            alt={item.name}
                            referrerPolicy="no-referrer-when-downgrade"
                            src={item.icon}
                          />
                        ) : (
                          <item.icon aria-hidden="true" />
                        )}
                        <span>{item.name}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ) : null,
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <ReportBug>
              <SidebarMenuButton
                type="button"
                className="text-sidebar-foreground hover:text-sidebar-accent-foreground cursor-pointer"
              >
                <MessagesSquare className="shrink-0 w-6 h-6" />
                <span>Ayuda</span>
              </SidebarMenuButton>
            </ReportBug>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => signOut().then(() => navigate("/login"))}
              className="text-sidebar-foreground hover:text-sidebar-accent-foreground cursor-pointer"
            >
              <LogOut className="shrink-0 w-6 h-6" />
              <span>Cerrar sesión</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>

        <div className="flex shrink-0 p-4 border-t border-sidebar-border">
          <Link to="/profile" className="shrink-0 block w-full group">
            <div className="flex items-center">
              <div>
                <Avatar>
                  <AvatarFallback>
                    {profile?.first_name[0]} {profile?.last_name[0]}{" "}
                  </AvatarFallback>
                </Avatar>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-sidebar-foreground group-hover:text-sidebar-accent-foreground">
                  {profile?.first_name} {profile?.last_name}
                </p>
                <p className="text-xs font-medium text-sidebar-foreground group-hover:text-sidebar-accent-foreground">
                  Ver perfil
                </p>
              </div>
            </div>
          </Link>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
